import {
  getCheckboxFilterDataSlug,
  hasCheckboxFilter,
  hasSliderFilter,
  getSliderFilterData,
} from '@/services/filter-layer-service'
import { FILTER_KEYS } from '@/services/filter-layer-service/constants'
import { SLUGS, FIELDS } from '@/fish/lib/taxonomy-constants'
import {
  gqlFilterSlugInList,
  gqlFilterSlugEquals,
  gqlFiltersAnd,
  gqlBraceWrapper,
  gqlFilterLT,
  gqlFilterGT,
  gqlFilterBetween,
} from '@/lib/graph-filter-utils'
import type { MinMaxData } from '@/lib/graph-filter-utils-types.ts'
import { acresToSqMeters, hectaresToSqMeters } from '@/lib/area'
import { getItem } from '@/plugins/storage'
import { Vertical } from '@/types/verticals'
import { FishMode } from '@/types/modes'

/**
 * Returns the list of selected slugs for a given filter key, or an empty array if no filter is active.
 */
export const getSlugListToFilter = (slug: string) => {
  const itemsToFilter: string[] = []
  const hasFilter = hasCheckboxFilter(FILTER_KEYS[slug])
  if (hasFilter) {
    const filterdata = getCheckboxFilterDataSlug(
      Vertical.Fish,
      FishMode.Freshwater,
      FILTER_KEYS[slug]
    )
    itemsToFilter.push(...filterdata)
  }
  return itemsToFilter
}

/**
 * Create filter for "Boat Launch" filters
 * These items are grouped via the `in` operator
 */
export const createLaunchFilter = (): string => {
  const launchesToFilter = getSlugListToFilter(SLUGS.LAUNCH)
  let launchExpression = ''
  if (launchesToFilter.length) {
    launchExpression = gqlFilterSlugInList(FIELDS.AMENITIES, launchesToFilter)
  }
  return launchExpression
}

/**
 * Create filter for "Surface Type" filters
 * These items are grouped via the `in` operator
 */
export const createSurfaceTypeFilter = (): string => {
  let surfaceExpression = ''
  const surfacesToFilter = getSlugListToFilter(SLUGS.RAMP)
  if (surfacesToFilter.length) {
    surfaceExpression = gqlFilterSlugInList(FIELDS.AMENITIES, surfacesToFilter)
  }
  return surfaceExpression
}

/**
 * Create filter for "Access and Amenities" filters
 * These items are grouped via the `and` operator
 */
export const createAccessAmenitiesFilters = (): string => {
  let amenitiesExpression = ''
  const amenitiesToFilter = getSlugListToFilter(FIELDS.AMENITIES)

  if (amenitiesToFilter.length) {
    amenitiesToFilter.forEach((slug: string, index: number) => {
      // Add comma to all but last expression
      const includeComma = index !== amenitiesToFilter.length - 1
      amenitiesExpression += gqlBraceWrapper(
        gqlFilterSlugEquals(FIELDS.AMENITIES, slug),
        includeComma
      )
    })
  }

  return amenitiesExpression
}

/**
 * Create GQL filter for water access sites
 */
export const buildWaterAccessFilters = (): string => {
  const launchExpression = gqlBraceWrapper(createLaunchFilter())
  const surfaceExpression = gqlBraceWrapper(createSurfaceTypeFilter())
  const accessAmenitiesExpression = createAccessAmenitiesFilters()
  const allExpressions = gqlFiltersAnd(
    launchExpression,
    surfaceExpression,
    accessAmenitiesExpression
  )
  return allExpressions ? `${FIELDS.ACCESS_SITES}: { ${allExpressions}  }` : ''
}

/**
 * Returns the filter field names for each fish toggle that is currently active.
 */
export const getActiveFilterToggles = (): string[] => {
  interface Toggle {
    storageId: string
    filterField: string
  }

  const filterToggles: Toggle[] = [
    {
      storageId: 'fish_freshwater_filter_trophy',
      filterField: FIELDS.TROPHY_FISH,
    },
    {
      storageId: 'fish_freshwater_filter_abundance',
      filterField: FIELDS.ABUNDANT_FISH,
    },
    {
      storageId: 'fish_freshwater_filter_keeper',
      filterField: FIELDS.KEEPER_FISH,
    },
    {
      storageId: 'fish_freshwater_filter_stocking_effort',
      filterField: FIELDS.STOCKING_EFFORT,
    },
  ]

  const toggleFilters: string[] = []
  filterToggles.forEach((toggle) => {
    if (getItem(toggle.storageId)) {
      toggleFilters.push(toggle.filterField)
    }
  })
  return toggleFilters
}

/**
 * Convert area data to correct units
 */
export const convertAreaData = (data: MinMaxData): MinMaxData => {
  const { min, max } = data
  const isImperial = getItem('unitType') === 'imperial'
  if (isImperial) {
    return { min: acresToSqMeters(min), max: acresToSqMeters(max) }
  }
  return { min: hectaresToSqMeters(min), max: hectaresToSqMeters(max) }
}

/**
 * Return any selected fish species slugs from the filter-layer service
 */
export const determineSpeciesToFilter = (): string[] => {
  const fishTypesToFilter: string[] = []
  const hasFishTypesFilter = hasCheckboxFilter(FILTER_KEYS[FIELDS.FISH_TYPES])
  if (hasFishTypesFilter) {
    const filterdata = getCheckboxFilterDataSlug(
      Vertical.Fish,
      FishMode.Freshwater,
      FILTER_KEYS[FIELDS.FISH_TYPES]
    )
    fishTypesToFilter.push(...filterdata)
  }
  return fishTypesToFilter
}

/**
 * Build GQL filter for fish types and active toggles
 */
export const buildFishTypesFilter = (): string => {
  let fishTypesExpression = ''
  const toggleFilters = getActiveFilterToggles()
  const fishTypesToFilter = determineSpeciesToFilter()
  if (fishTypesToFilter.length) {
    if (toggleFilters.length) {
      fishTypesExpression = toggleFilters
        .map((item: string) => gqlFilterSlugInList(item, fishTypesToFilter))
        .join(', ')
    } else {
      fishTypesExpression = gqlFilterSlugInList(
        FIELDS.FISH_TYPES,
        fishTypesToFilter
      )
    }
  }
  return fishTypesExpression
}

/**
 * Create GQL filter for waterbody area size
 */
export const buildAreaFilter = (): string => {
  let areaExpression = ''
  const hasAreaFilter = hasSliderFilter(FILTER_KEYS[FIELDS.AREA])
  if (hasAreaFilter) {
    const { hasMinFilter, hasMaxFilter } = hasAreaFilter
    const filterData = getSliderFilterData(FILTER_KEYS[FIELDS.AREA])
    const { min, max } = convertAreaData(filterData)
    if (hasMinFilter && hasMaxFilter) {
      areaExpression = gqlFilterBetween(FIELDS.AREA, min, max)
    } else if (hasMinFilter) {
      areaExpression = gqlFilterGT(FIELDS.AREA, min)
    } else if (hasMaxFilter) {
      areaExpression = gqlFilterLT(FIELDS.AREA, max)
    }
  }
  return areaExpression
}
